import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SupportNavbar from "../../components/support-components/SupportNavbar";
import SupportTopbar from "../../components/support-components/SupportTopbar";
import API_BASE_URL from "../../config";
import "../SalesPages.css";
import "./SupportDashboard.css";

function SupportDashboard() {
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [closedTickets, setClosedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClosed, setLoadingClosed] = useState(true);
  const [error, setError] = useState("");

  const fetchTickets = async (status = null) => {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams();

    if (status) {
      params.set("status", status);
    }

    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/support-tickets/admin${
      queryString ? `?${queryString}` : ""
    }`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || data.message || "Failed to load support tickets");
    }

    return Array.isArray(data) ? data : [];
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      setLoadingClosed(true);
      setError("");

      const [activeRows, closedRows] = await Promise.all([
        fetchTickets(),
        fetchTickets("Closed"),
      ]);

      setTickets(activeRows);
      setClosedTickets(closedRows);
    } catch (err) {
      setError(err.message || "Failed to load support tickets");
      setTickets([]);
      setClosedTickets([]);
    } finally {
      setLoading(false);
      setLoadingClosed(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const stats = useMemo(() => {
    return {
      active: tickets.length,
      open: tickets.filter((ticket) => ticket.status === "Open").length,
      inProgress: tickets.filter((ticket) => ticket.status === "In Progress").length,
      waiting: tickets.filter((ticket) => ticket.status === "Waiting on Customer").length,
      urgent: tickets.filter((ticket) => ticket.priority === "Urgent").length,
      resolved: tickets.filter((ticket) => ticket.status === "Resolved").length,
      closed: closedTickets.length,
    };
  }, [tickets, closedTickets]);

  const urgentTickets = useMemo(() => {
    return tickets
      .filter((ticket) => ticket.priority === "Urgent")
      .slice(0, 5);
  }, [tickets]);

  const recentTickets = useMemo(() => {
    return tickets.slice(0, 8);
  }, [tickets]);

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString();
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`;

  const goToQueue = (status = null) => {
    if (!status) {
      navigate("/support-admin/tickets");
      return;
    }

    navigate(`/support-admin/tickets?status=${encodeURIComponent(status)}`);
  };

  const renderTicketRows = (rows, emptyMessage, colSpan = 6) => {
    if (loading) {
      return (
        <tr>
          <td colSpan={colSpan} className="sales-empty-cell">
            Loading support tickets...
          </td>
        </tr>
      );
    }

    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="sales-empty-cell">
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return rows.map((ticket) => (
      <tr
        key={ticket.id}
        className="sales-click-row"
        onClick={() => navigate(`/support-admin/tickets/${ticket.id}`)}
      >
        <td>
          <div className="sales-primary-cell">
            <div className="sales-name">#{ticket.id} {ticket.subject}</div>
            <div className="sales-secondary">
              {ticket.tenant_type} #{ticket.tenant_id}
            </div>
          </div>
        </td>

        <td>{ticket.submitted_by_name || ticket.submitted_by_email || "—"}</td>
        <td>{ticket.category || "—"}</td>
        <td>{ticket.priority || "—"}</td>
        <td>
          <span className={statusClass(ticket.status)}>
            {ticket.status || "—"}
          </span>
        </td>
        <td>{formatDate(ticket.created_at)}</td>
      </tr>
    ));
  };

  return (
    <>
      <SupportNavbar />
      <SupportTopbar />

      <div className="main-content sales-page">
        <div className="sales-hero support-dashboard-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM Support</p>
            <h1 className="sales-title">Support Dashboard</h1>
            <p className="sales-subtitle">
              Monitor active support work, urgent requests, and recently submitted tickets.
            </p>
          </div>

          <div className="sales-header-actions">
            <button className="sales-secondary-btn" onClick={loadTickets}>
              Refresh
            </button>

            <button
              className="sales-primary-btn"
              onClick={() => navigate("/support-admin/tickets")}
            >
              View Queue
            </button>
          </div>
        </div>

        {error && (
          <div className="sales-section-card">
            {error}
          </div>
        )}

        <div className="support-dashboard-grid">
          <div className="sales-table-card support-dashboard-summary-card">
            <div className="sales-table-header">
              <div>
                <h2>Workload</h2>
                <p>Click a row to open the matching ticket queue.</p>
              </div>
            </div>

            <div className="support-dashboard-metric-list">
              <button type="button" onClick={() => goToQueue()}>
                <span>Active Queue</span>
                <strong>{loading ? "—" : stats.active}</strong>
              </button>

              <button type="button" onClick={() => goToQueue("Open")}>
                <span>Open</span>
                <strong>{loading ? "—" : stats.open}</strong>
              </button>

              <button type="button" onClick={() => goToQueue("In Progress")}>
                <span>In Progress</span>
                <strong>{loading ? "—" : stats.inProgress}</strong>
              </button>

              <button type="button" onClick={() => goToQueue("Waiting on Customer")}>
                <span>Waiting on Customer</span>
                <strong>{loading ? "—" : stats.waiting}</strong>
              </button>

              <button type="button" onClick={() => goToQueue("Resolved")}>
                <span>Resolved</span>
                <strong>{loading ? "—" : stats.resolved}</strong>
              </button>

              <button type="button" onClick={() => goToQueue("Closed")}>
                <span>Closed</span>
                <strong>{loadingClosed ? "—" : stats.closed}</strong>
              </button>
            </div>
          </div>

          <div className="sales-table-card support-dashboard-urgent-card">
            <div className="sales-table-header">
              <div>
                <h2>Urgent Tickets</h2>
                <p>{stats.urgent} urgent active ticket{stats.urgent === 1 ? "" : "s"}</p>
              </div>

              <button
                type="button"
                className="sales-secondary-btn"
                onClick={() => goToQueue()}
              >
                Open Queue
              </button>
            </div>

            <div className="sales-table-wrap">
              <table className="sales-table support-dashboard-small-table">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="3" className="sales-empty-cell">
                        Loading urgent tickets...
                      </td>
                    </tr>
                  ) : urgentTickets.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="sales-empty-cell">
                        No urgent active tickets.
                      </td>
                    </tr>
                  ) : (
                    urgentTickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        className="sales-click-row"
                        onClick={() => navigate(`/support-admin/tickets/${ticket.id}`)}
                      >
                        <td>
                          <div className="sales-primary-cell">
                            <div className="sales-name">#{ticket.id} {ticket.subject}</div>
                            <div className="sales-secondary">
                              {ticket.submitted_by_name || ticket.submitted_by_email || "—"}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={statusClass(ticket.status)}>
                            {ticket.status || "—"}
                          </span>
                        </td>
                        <td>{formatDate(ticket.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>Recent Active Tickets</h2>
              <p>
                {tickets.length} active ticket{tickets.length === 1 ? "" : "s"} excluding Closed
              </p>
            </div>

            <button
              type="button"
              className="sales-secondary-btn"
              onClick={() => navigate("/support-admin/tickets")}
            >
              View All
            </button>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Submitted By</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>

              <tbody>
                {renderTicketRows(
                  recentTickets,
                  "No active support tickets found."
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default SupportDashboard;